import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  FileText,
  Calendar,
  User,
  Hash,
  Eye,
  Clock
} from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentTenantSlug } from "@/utils/tenant-utils";
import { WorkspaceService } from "@/services/workspace-service";

interface FormSubmission {
  id: string;
  form_title: string;
  submission_data: any;
  submitted_at: string;
  submitted_by: string | null;
  submission_number: number;
  config_id: string;
  submission_name?: string;
}

interface ManualConfig {
  id: string;
  form_title: string;
  manufacturing_process_id: string;
  config_data: {
    fields: Array<{
      id: string;
      title: string;
      type: string;
      required: boolean;
      options?: string[];
    }>;
    formTitle: string;
  };
}

interface ManufacturingProcess {
  id: string;
  name: string;
}

interface FormDataViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  station: ManufacturingProcess | null;
  formTitle: string;
  configId: string;
  manualConfigs: ManualConfig[];
}

const FormDataViewerModal = ({ isOpen, onClose, station, formTitle, configId, manualConfigs }: FormDataViewerModalProps) => {
  
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string>('');
  const [selectedSubmission, setSelectedSubmission] = useState<FormSubmission | null>(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // Get the config for this form to understand the field structure
  const formConfig = useMemo(() => {
    return manualConfigs.find(config => config.id === configId);
  }, [manualConfigs, configId]);

  // Fetch submissions when modal opens
  useEffect(() => {
    if (isOpen && configId && user) {
      fetchSubmissions();
    }
  }, [isOpen, configId, user]);

  // Update selected submission when selection changes
  useEffect(() => {
    if (selectedSubmissionId) {
      const submission = submissions.find(s => s.id === selectedSubmissionId);
      setSelectedSubmission(submission || null);
    } else {
      setSelectedSubmission(null);
    }
  }, [selectedSubmissionId, submissions]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedSubmissionId('');
      setSelectedSubmission(null);
      setSubmissions([]);
    }
  }, [isOpen]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      // Get current workspace
      const currentSlug = getCurrentTenantSlug();
      
      const workspace = currentSlug ? await WorkspaceService.getWorkspaceBySlug(currentSlug) : null;
      
      if (!workspace) {
        console.error('❌ FormDataViewerModal: No workspace found');
        setSubmissions([]);
        return;
      }

      const { data, error } = await supabase
        .from('watt_form_submissions')
        .select('*')
        .eq('config_id', configId)
        .eq('workspace_id', workspace.id)
        .order('submission_number', { ascending: true }); // Order by submission number

      if (error) throw error;
      setSubmissions(data || []);
    } catch (error) {
      console.error('❌ FormDataViewerModal: Error fetching form submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const renderFieldValue = (field: any, value: any) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-muted-foreground italic">No data</span>;
    }

    switch (field.type) {
      case 'boolean':
        return (
          <Badge variant={value === true ? "default" : "secondary"}>
            {value === true ? 'Yes' : 'No'}
          </Badge>
        );
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'dropdown':
      case 'radio':
        return <Badge variant="outline">{value}</Badge>;
      case 'textarea':
        return (
          <div className="bg-muted p-2 rounded text-sm max-w-md">
            {value}
          </div>
        );
      default:
        return <span>{value}</span>;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            View {formTitle} Data
          </DialogTitle>
          <DialogDescription>
            View submitted {formTitle.toLowerCase()} data for {station?.name}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Submission Selection */}
          <div className="space-y-2">
            <Label>Select Submission</Label>
            {loading ? (
              <div className="text-center py-4 text-muted-foreground">
                Loading submissions...
              </div>
            ) : submissions.length > 0 ? (
              <Select
                value={selectedSubmissionId}
                onValueChange={setSelectedSubmissionId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a submission to view" />
                </SelectTrigger>
                <SelectContent>
                  {submissions.map((submission) => (
                    <SelectItem key={submission.id} value={submission.id}>
                      <div className="flex items-center gap-2">
                        <Hash className="w-4 h-4" />
                        <span>#{submission.submission_number}</span>
                        {submission.submission_name && (
                          <>
                            <span className="text-muted-foreground">-</span>
                            <span>{submission.submission_name}</span>
                          </>
                        )}
                        {!submission.submission_name && (
                          <>
                            <span className="text-muted-foreground">-</span>
                            <span>{formatDate(submission.submitted_at)}</span>
                          </>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No submissions found</p>
                <p className="text-sm">No {formTitle.toLowerCase()} data has been submitted yet</p>
              </div>
            )}
          </div>

          {/* Submission Details */}
          {selectedSubmission && formConfig && (
            <div className="space-y-4">
              <div className="border-t pt-4">
                {/* Submission Header */}
                <Card className="mb-4">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        {formTitle} #{selectedSubmission.submission_number}
                      </span>
                      <Badge variant="outline">
                        {formatDate(selectedSubmission.submitted_at)}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Submitted: {formatDate(selectedSubmission.submitted_at)}
                      </div>
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        Submission #{selectedSubmission.submission_number}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Form Data */}
                <Card>
                  <CardHeader>
                    <CardTitle>Form Data</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4">
                      {formConfig.config_data.fields.map((field) => (
                        <div key={field.id} className="grid grid-cols-3 gap-4 py-2 border-b border-border last:border-b-0">
                          <div className="font-medium text-sm">
                            {field.title}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                          </div>
                          <div className="col-span-2">
                            {renderFieldValue(field, selectedSubmission.submission_data[field.id])}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { FormDataViewerModal }; 